import apollo from '../../apollo';

import { CollectionFlag } from '../../types/collection';
import { AsyncActionHandler } from '../../action-handler';
import { getPagedCollectionQuery, getPagedCategoryQuery } from '../../queries';
import {
  createNormalizedMap,
  handleFetchError,
  getNextPageKey,
  hasNextPage,
  updatePages,
  pushApps
} from '../../action-utils';
import { CollectionType } from '../../constants';

export default new AsyncActionHandler({
  name: 'fetch_collection',
  stateProp: 'fetchCollection',
  defaultState: {
    collections: {}
  },
  // Disable eslint rather than modifying all occurrences.
  // eslint-disable-next-line
  handler(id, type, startIndex = 0, userFlags = [], resetCache = false) {
    return async (_, getState) => {
      const {
        collections: { [id]: savedCollection }
      } = getState().registry;

      // Get the next pageKey to know what page to request next(don't get the
      // pageKey if a cache reset was requested).
      const pageKey = resetCache
        ? undefined
        : getNextPageKey({
            pages: savedCollection && savedCollection.pages
          });

      // If no more pages to load, then return.
      if (startIndex !== 0 && !pageKey) {
        return;
      }

      if (!userFlags) {
        if (savedCollection && savedCollection.userFlags) {
          // Load the previously selected user flags (i.e: FilterType
          // enum values)
          userFlags = savedCollection.userFlags;
        } else {
          // No filters fetch everything.
          userFlags = [];
        }
      }

      let collection;
      try {
        const { data } = await apollo.query({
          query:
            type === CollectionType.COLLECTION
              ? getPagedCollectionQuery
              : getPagedCategoryQuery,
          variables: { id, pageKey, userFlags }
          // Uncomment to disable caching. Default is 'cache-first'.
          // fetchPolicy: resetCache ? 'no-cache' : 'cache-first',
        });
        collection = data.collection;
      } catch (error) {
        return handleFetchError(error);
      }

      if (!collection) {
        throw new Error('no collection');
      }

      return {
        ...collection,
        type,
        pageKey,
        resetCache,
        userFlags
      };
    };
  },
  reduce: (
    state,
    {
      id,
      name,
      flags,
      pagedApps,
      type,
      pageKey = 0,
      resetCache,
      userFlags
    } = {}
  ) => {
    if (!pagedApps) {
      return;
    }

    const collection = state.collections[id] || {};

    const existingAppIds = collection.appIds || [];
    /* Reset collection apps if the first page was requested
     * because the filtering options might have changed
     * and we don't get the same apps. */
    if (resetCache) {
      // Remove existing apps.
      existingAppIds.splice(0, existingAppIds.length);
      // Remove existing pages.
      collection.pages = [];
    }
    const appIds = pushApps(existingAppIds, pagedApps.apps);

    const pages = updatePages({
      pagedApps,
      startIndex: existingAppIds.length,
      pages: collection.pages,
      pageKey
    });
    return {
      collections: {
        ...state.collections,
        [id]: {
          ...collection,
          id,
          name,
          isPremium: flags && flags.includes(CollectionFlag.PREMIUM),
          appIds,
          hasNextPage: hasNextPage(pages),
          pages,
          type,
          userFlags
        }
      },
      apps: {
        ...state.apps,
        ...createNormalizedMap(pagedApps.apps, state.apps)
      }
    };
  }
});
